import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ScanFace } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [0, 2, 4, 7, 10];

// Super Admin / Temple Admin: face-recognition duplicate check for Annaprasadam tokens (per temple).
export default function AnnaFaceSettings({ templeId }) {
  const [days, setDays] = useState(null);
  const [choice, setChoice] = useState("0");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!templeId) return;
    api.get("/anna/settings", { params: { temple_id: templeId } }).then((r) => {
      const d = Number(r.data.face_days) || 0;
      setDays(d);
      setChoice(PRESETS.includes(d) ? String(d) : "custom");
      setCustom(PRESETS.includes(d) ? "" : String(d));
    }).catch(() => {});
  }, [templeId]);

  if (!templeId || days === null) return null;
  const value = choice === "custom" ? Number(custom) : Number(choice);
  const save = async () => {
    if (!Number.isInteger(value) || value < 0 || value > 365) { toast.error("Enter a number of days between 1 and 365"); return; }
    setSaving(true);
    try {
      const r = await api.put("/anna/settings", { temple_id: templeId, face_days: value });
      setDays(r.data.face_days);
      toast.success(r.data.face_days ? `Face check ON: one token per devotee every ${r.data.face_days} days` : "Face check turned off");
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  return (
    <Card className="shadow-sm" data-testid="anna-face-settings">
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <ScanFace className="w-6 h-6 text-maroon-700" />
        <div className="min-w-[220px] flex-1">
          <p className="font-heading font-bold text-maroon-900">Face Recognition · Duplicate Token Check</p>
          <p className="text-xs text-slate-500">
            {days ? `ON - a devotee gets one token every ${days} days (same face is blocked).` : "OFF - tokens are issued without a face check."}
            {" "}Face templates are deleted automatically after the restriction period.
          </p>
        </div>
        <select value={choice} onChange={(e) => setChoice(e.target.value)} className="h-9 rounded-md border border-[#E5D9C5] bg-white px-2 text-sm" data-testid="anna-face-days">
          <option value="0">Off</option>
          {[2, 4, 7, 10].map((d) => <option key={d} value={String(d)}>{d} days</option>)}
          <option value="custom">Custom number of days</option>
        </select>
        {choice === "custom" && (
          <input type="number" min={1} max={365} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Days"
            className="h-9 w-24 rounded-md border border-[#E5D9C5] bg-white px-2 text-sm" data-testid="anna-face-custom" />
        )}
        <Button onClick={save} disabled={saving || value === days} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="anna-face-save">Save</Button>
      </CardContent>
    </Card>
  );
}
